import { Button } from "@benefit-systems/common-components";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Loading from "web/Layout/Common/Loading";
import __ from "web/Layout/Translations";

import Download from "web/assets/icons/download.svg";
import PrinterIcon from "web/assets/icons/printer.svg";

import useInternetConnection from "web/hooks/useInternetConnection/useInternetConnection";

import formatDate from "web/utils/data/parser/dateAndTime/formatDate";
import formatHours from "web/utils/data/parser/dateAndTime/formatHours";
import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";

import restUrls from "web/constants/restUrls";

import { SupplierID } from "web/types/SupplierData";
import { Nullable } from "web/types/Utils";

import { request } from "web/api";
import { useAppContext } from "web/context/app";
import {
  OutcomingWebViewMessageType,
  sendMessageToNativeApp,
} from "web/context/webView";
import { setNotificationSuccess } from "web/features/app/appSlice";
import { useGetSupplierDataQuery } from "web/features/customer/customerApiSlice";

import classes from "./codeModal.scss";

export const enum CodeActionType {
  SET_CAN_PRINT = "SET_CAN_PRINT",
  GET_CAN_PRINT = "GET_CAN_PRINT",
  SET_CODE = "SET_CODE",
  GET_CODE = "GET_CODE",
  SET_BARCODE = "SET_BARCODE",
  GET_BARCODE = "GET_BARCODE",
}

const enum SessionStorageActionType {
  SET = "SET",
  GET = "GET",
}

type Key = string;
type ItemID = number;
type SessionStorageItemValue = string | boolean;

interface IGetSessionStorageArgs {
  action: SessionStorageActionType.GET;
  key: Key;
}

interface ISetSessionStorageArgs {
  action: SessionStorageActionType.SET;
  key: Key;
  value: SessionStorageItemValue;
}

interface HandleSessionStorageAction {
  (args: IGetSessionStorageArgs): Nullable<string>;
  (args: ISetSessionStorageArgs): void;
}

interface IGetCodeActionArgs {
  action:
    | CodeActionType.GET_BARCODE
    | CodeActionType.GET_CAN_PRINT
    | CodeActionType.GET_CODE;
  itemId: number;
}

interface ISetCodeActionArgs {
  action:
    | CodeActionType.SET_BARCODE
    | CodeActionType.SET_CAN_PRINT
    | CodeActionType.SET_CODE;
  value: string | boolean;
  itemId: number;
}

export interface HandleCodeSessionStorageAction {
  (args: IGetCodeActionArgs): Nullable<string>;
  (args: ISetCodeActionArgs): void;
}

const handleSessionStorageAction: HandleSessionStorageAction = (args) => {
  const { action, key } = args;

  switch (action) {
    case SessionStorageActionType.GET:
      return sessionStorage.getItem(key);
    case SessionStorageActionType.SET:
      sessionStorage.setItem(key, `${args.value}`);
      return null;
    default:
      return null;
  }
};

interface ICodeModalContainerProps {
  productName: string;
  itemId: ItemID;
  supplierId: SupplierID;
  orderPaid: boolean;
}

const handleCodeSessionStorageAction: HandleCodeSessionStorageAction = ({
  itemId,
  ...args
}) => {
  switch (args.action) {
    case CodeActionType.SET_CAN_PRINT:
      handleSessionStorageAction({
        action: SessionStorageActionType.SET,
        key: `${itemId}_CAN_PRINT`,
        value: args.value,
      });
      return null;
    case CodeActionType.GET_CAN_PRINT:
      return handleSessionStorageAction({
        action: SessionStorageActionType.GET,
        key: `${itemId}_CAN_PRINT`,
      });
    case CodeActionType.SET_CODE:
      handleSessionStorageAction({
        action: SessionStorageActionType.SET,
        key: `${itemId}_PRODUCT_CODE`,
        value: args.value,
      });
      return null;
    case CodeActionType.GET_CODE:
      return handleSessionStorageAction({
        action: SessionStorageActionType.GET,
        key: `${itemId}_PRODUCT_CODE`,
      });
    case CodeActionType.SET_BARCODE:
      handleSessionStorageAction({
        action: SessionStorageActionType.SET,
        key: `${itemId}_BARCODE`,
        value: args.value,
      });
      return null;
    case CodeActionType.GET_BARCODE:
      return handleSessionStorageAction({
        action: SessionStorageActionType.GET,
        key: `${itemId}_BARCODE`,
      });
    default:
      return null;
  }
};

const CodeModalContainer: FC<ICodeModalContainerProps> = ({
  productName,
  itemId,
  supplierId,
  orderPaid,
}) => {
  const dispatch = useDispatch();

  const [code, setCode] = useState(
    handleCodeSessionStorageAction({ action: CodeActionType.GET_CODE, itemId })
  );
  const [barCode, setBarCode] = useState(
    handleCodeSessionStorageAction({
      action: CodeActionType.GET_BARCODE,
      itemId,
    })
  );
  const [canPrintCode, setCanPrintCode] = useState(
    !!handleCodeSessionStorageAction({
      action: CodeActionType.GET_CAN_PRINT,
      itemId,
    })
  );
  const [isPrinting, setIsPrinting] = useState(false);

  const codeInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useAppContext();
  const { data: supplierData, isLoading: loading } =
    useGetSupplierDataQuery(supplierId);

  const { isDisconnectedWebView } = useInternetConnection();

  const supplierSettingCanPrintCode = supplierData?.settings?.canPrintCode;

  // eslint-disable-next-line no-underscore-dangle
  const _code = code || "";

  useEffect(() => {
    if (!canPrintCode && supplierSettingCanPrintCode) {
      handleCodeSessionStorageAction({
        itemId,
        action: CodeActionType.SET_CAN_PRINT,
        value: supplierSettingCanPrintCode,
      });
      setCanPrintCode(supplierSettingCanPrintCode);
    }
  }, [canPrintCode, supplierSettingCanPrintCode]);

  const copyToClipboard = () => {
    if (window.ReactNativeWebView) {
      codeInputRef.current?.select();
      document.execCommand("copy");
    } else {
      window.navigator.clipboard.writeText(_code);
    }
    dispatch(setNotificationSuccess({ message: __("Kod został skopiowany") }));
  };

  useEffect(() => {
    const getBarcode = async () => {
      try {
        const response = await request(
          `${restUrls.barCodeImage}?itemId=${Number(itemId)}`
        );
        const barCode = `data:image/png;base64,${response}`;
        handleCodeSessionStorageAction({
          itemId,
          action: CodeActionType.SET_BARCODE,
          value: barCode,
        });
        setBarCode(barCode);
      } catch (error) {
        console.error(error);
        newRelicErrorReport(
          error,
          "web-app/web/Components/Common/CodeItem/CodeModal/codeModal.tsx - 224"
        );
      }
    };
    if (canPrintCode && !barCode && orderPaid) {
      getBarcode();
    }
  }, [itemId, canPrintCode, barCode, orderPaid]);

  useEffect(() => {
    const getCode = async () => {
      try {
        const response = await request(
          `${restUrls.productCode}?item_id=${Number(itemId)}`
        );
        const productCode = (response as { code: string }[])[0].code;
        handleCodeSessionStorageAction({
          itemId,
          action: CodeActionType.SET_CODE,
          value: productCode,
        });
        setCode(productCode);
      } catch (error) {
        console.error(error);
      }
    };
    if (!code && orderPaid) {
      getCode();
    }
  }, [itemId, code, orderPaid]);

  const openInNewTab = (url: string) => {
    const fullUrl = `${window.location.protocol}//${window.location.host}/${url}`;
    const newWindow = window.open(fullUrl, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };
  const print = useCallback(async () => {
    try {
      setIsPrinting(true);
      const response = await request(`${restUrls.getGiftPdf}?itemId=${itemId}`);
      openInNewTab(response as string);
    } catch (errorData) {
      newRelicErrorReport(
        errorData,
        "web-app/web/Components/Common/CodeItem/CodeModal/codeModal.tsx - 265"
      );
      console.error(errorData);
    } finally {
      setIsPrinting(false);
    }
  }, [itemId]);

  const onPrintCodeDirectlyHandler = () => {
    if (!window.ReactNativeWebView) {
      window.print();
      return;
    }
    const now = Date.now();
    const html = `
                  <div style="display:flex;flex-direction:column;">
                    <div style="font-size:10px;display:flex;justify-content:space-between;flex-wrap:wrap;margin-bottom:32px">
                      <div>${formatDate(now)}, ${formatHours(now)}</div>
                      <div>${window.location}</div>
                    </div>
                    <div style="text-align:center;">
                      <h1 style="font-size:16px;">
                        ${__("Twój kod")}
                      </h1>
                      <h2 style="font-size:20px;">
                        ${productName}
                      </h1>
                      <p style="font-size:14px;">
                        ${code}
                      </p>
                    </div>
                  </div>
                `;

    sendMessageToNativeApp({
      type: OutcomingWebViewMessageType.CONVERT_TO_PDF,
      payload: {
        html,
        pdfFileName: __("Moje zamówienia"),
      },
    });
  };

  const inAppPrintingBtn = code ? (
    <Button
      variant="secondary"
      transparent
      onClick={onPrintCodeDirectlyHandler}
      dataT1="print_button"
    >
      <>
        <Download className={classes.icon} />
        {__("Pobierz kod")}
      </>
    </Button>
  ) : (
    <Loading />
  );

  const buttons = isMobile ? (
    <>
      {canPrintCode ? (
        <>
          {isPrinting ? (
            <Loading />
          ) : (
            <Button
              variant="secondary"
              transparent
              onClick={print}
              dataT1="print_button"
            >
              <>
                <Download className={classes.icon} />
                {__("Pobierz kod kreskowy")}
              </>
            </Button>
          )}
        </>
      ) : (
        inAppPrintingBtn
      )}
    </>
  ) : (
    <>
      {canPrintCode ? (
        <>
          {isPrinting ? (
            <Loading />
          ) : (
            <Button
              variant="secondary"
              transparent
              onClick={print}
              dataT1="print_button"
            >
              <>
                <PrinterIcon className={classes.icon} />
                {__("Drukuj kod kreskowy")}
              </>
            </Button>
          )}
        </>
      ) : (
        <Button
          variant="secondary"
          transparent
          onClick={window.print}
          dataT1="print_button"
        >
          <>
            <PrinterIcon className={classes.icon} />
            {__("Drukuj")}
          </>
        </Button>
      )}
    </>
  );

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.buttons}>{buttons}</div>
        <div className={classes.title} data-t1="code_modal_title">
          <h2 className={classes.header} data-t1="code_modal_header">
            {__("Twój kod")}
          </h2>
          <h2 className={classes.productName} data-t1="code_modal_product_name">
            {productName}
          </h2>
        </div>
        <div className={classes.box}>
          {loading ? (
            <Loading />
          ) : (
            <>
              {barCode && canPrintCode ? (
                <div className={classes.codeImageContainer}>
                  <img
                    className={classes.codeImage}
                    src={barCode}
                    alt={__("Kod kreskowy")}
                  />
                </div>
              ) : null}
              {code ? (
                <div className={classes.code} data-t1="code_modal_code">
                  {code}
                </div>
              ) : null}
              {isDisconnectedWebView && (!barCode || !code) && (
                <p>{__("Brak połączenia z siecią")}</p>
              )}
            </>
          )}

          <form className={classes.copy} data-t1="code_modal_copy">
            <input
              ref={codeInputRef}
              value={_code}
              className="visually-hidden"
              data-t1="code_modal_input"
              readOnly
            />
            <Button
              variant="secondary"
              onClick={copyToClipboard}
              dataT1="code_modal_button"
              transparent
            >
              {__("Kopiuj kod")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CodeModalContainer;
